
import React, { useEffect, useRef, useState } from "react";
import { usePartners } from "@/context/PartnerContext";
import { useLeads } from "@/context/LeadContext";
import { loadGoogleMapsScript } from "@/utils/loadGoogleMapsScript";
import { useIsMobile } from "@/hooks/use-mobile";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Info, MapPin, RefreshCw, X, Search, CheckCircle2, User, Phone, Map } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const PartnersMap: React.FC = () => {
  const { partners, loading: partnersLoading, refreshPartners } = usePartners();
  const { leads } = useLeads();
  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<{ [key: string]: google.maps.Marker }>({});
  const circlesRef = useRef<{ [key: string]: google.maps.Circle }>({});
  const infoWindowsRef = useRef<{ [key: string]: google.maps.InfoWindow }>({});

  const [mapsLoaded, setMapsLoaded] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const isMobile = useIsMobile();

  useEffect(() => {
    const initMaps = async () => {
      try {
        await loadGoogleMapsScript();
        setMapsLoaded(true);
      } catch (error) {
        console.error("Failed to load Google Maps:", error);
      }
    };

    initMaps();
  }, []);

  useEffect(() => {
    if (mapsLoaded && mapRef.current) {
      initializeMap();
    }
  }, [mapsLoaded]);

  useEffect(() => {
    if (googleMapRef.current && partners.length > 0) {
      renderPartners();
    }
  }, [partners, googleMapRef.current]);

  const initializeMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps) return;

    // Greece coordinates
    const greeceCenter = { lat: 38.7, lng: 24 };

    const mapOptions: google.maps.MapOptions = {
      center: greeceCenter,
      zoom: 7,
      styles: [
        {
          "featureType": "poi",
          "elementType": "labels",
          "stylers": [{ "visibility": "off" }]
        }
      ],
      mapTypeControl: false,
      fullscreenControl: true,
      streetViewControl: false,
      zoomControl: true,
      zoomControlOptions: {
        position: google.maps.ControlPosition.RIGHT_CENTER
      }
    };

    googleMapRef.current = new window.google.maps.Map(mapRef.current, mapOptions);

    if (partners.length > 0) {
      renderPartners();
    }
  };

  const renderPartners = () => {
    if (!googleMapRef.current) return;

    // Clear existing markers and circles
    Object.values(markersRef.current).forEach(marker => marker.setMap(null));
    Object.values(circlesRef.current).forEach(circle => circle.setMap(null));
    markersRef.current = {};
    circlesRef.current = {};
    infoWindowsRef.current = {};

    // Add markers for each partner
    partners.forEach(partner => {
      if (!partner.latitude || !partner.longitude) return;
      const lat = Number(partner.latitude);
      const lng = Number(partner.longitude);
      if (isNaN(lat) || isNaN(lng)) return;

      const position = { lat, lng };

      // Count leads assigned to this partner
      const partnerLeads = leads.filter(lead => lead.partnerId === partner.id);
      const successfulLeads = partnerLeads.filter(lead => lead.stage === 'έκλεισεΗΔουλειά');

      // Create custom marker icon with different colors based on status
      const pinColor = partner.active ? "#3b82f6" : "#94a3b8";

      const marker = new google.maps.Marker({
        position,
        map: googleMapRef.current,
        title: partner.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          fillColor: pinColor,
          fillOpacity: 0.9,
          strokeWeight: 2,
          strokeColor: "#FFFFFF",
          scale: 9
        },
        animation: google.maps.Animation.DROP,
        zIndex: partner.active ? 10 : 5
      });

      // Create circle showing coverage radius
      const circle = new google.maps.Circle({
        strokeColor: pinColor,
        strokeOpacity: 0.5,
        strokeWeight: 1,
        fillColor: pinColor,
        fillOpacity: 0.1,
        map: googleMapRef.current,
        center: position,
        radius: (partner.radius || 10) * 1000,
        zIndex: 1
      });

      // Create info window with partner details
      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div class="p-3 min-w-[200px] font-sans">
            <div class="flex items-center gap-2 mb-2 pb-2 border-b border-gray-100">
               <div class="font-bold text-base text-slate-800">${partner.name}</div>
               ${partner.active ?
            '<span class="bg-emerald-100 text-emerald-800 text-[10px] px-2 py-0.5 rounded-full font-medium">Ενεργός</span>' :
            '<span class="bg-gray-100 text-gray-600 text-[10px] px-2 py-0.5 rounded-full font-medium">Ανενεργός</span>'
          }
            </div>
            
            <div class="space-y-1.5 text-sm text-slate-600">
              <div class="flex items-start gap-1.5">
                <span>📞</span>
                <span class="font-medium text-slate-900">${partner.phone}</span>
              </div>
              <div class="flex items-start gap-1.5">
                <span>📍</span>
                <span class="text-xs leading-tight">${partner.location}</span>
              </div>
              <div class="grid grid-cols-2 gap-2 mt-2 pt-2 border-t border-gray-50">
                <div class="bg-slate-50 p-1.5 rounded text-center">
                  <div class="text-[10px] uppercase text-slate-400 font-semibold">ΚΑΛΥΨΗ</div>
                  <div class="font-bold text-slate-900">${partner.radius} χλμ</div>
                </div>
                <div class="bg-emerald-50 p-1.5 rounded text-center">
                  <div class="text-[10px] uppercase text-emerald-600 font-semibold">ΕΠΙΤΥΧΙΕΣ</div>
                  <div class="font-bold text-emerald-700">${successfulLeads.length}</div>
                </div>
              </div>
            </div>
          </div>
        `,
        pixelOffset: new google.maps.Size(0, -10)
      });

      // Store references
      markersRef.current[partner.id] = marker;
      circlesRef.current[partner.id] = circle;
      infoWindowsRef.current[partner.id] = infoWindow;

      // Add click event
      google.maps.event.addListener(marker, 'click', () => {
        // Close any open info windows
        Object.values(infoWindowsRef.current).forEach(window => window.close());

        // Open this info window
        infoWindow.open(googleMapRef.current, marker);

        // Set selected partner
        setSelectedPartner(partner.id);
      });
    });

    // Fit map to show all markers if there are any
    if (Object.keys(markersRef.current).length > 0) {
      const bounds = new google.maps.LatLngBounds();

      Object.values(markersRef.current).forEach(marker => {
        if (marker.getPosition()) {
          bounds.extend(marker.getPosition()!);
        }
      });

      googleMapRef.current.fitBounds(bounds, 50); // 50px padding

      // Don't zoom in too far
      if (googleMapRef.current.getZoom()! > 12) {
        googleMapRef.current.setZoom(12);
      }
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refreshPartners();
    setIsRefreshing(false);
  };

  const clearSelection = () => {
    setSelectedPartner(null);
    Object.values(infoWindowsRef.current).forEach(window => window.close());

    // Reset view
    if (googleMapRef.current && Object.keys(markersRef.current).length > 0) {
      const bounds = new google.maps.LatLngBounds();
      Object.values(markersRef.current).forEach(marker => {
        if (marker.getPosition()) bounds.extend(marker.getPosition()!);
      });
      googleMapRef.current.fitBounds(bounds, 50);
    }
  };

  const zoomToPartner = (partnerId: string) => {
    const marker = markersRef.current[partnerId];
    if (marker && googleMapRef.current) {
      googleMapRef.current.setZoom(11);
      googleMapRef.current.panTo(marker.getPosition()!);

      // Open info window
      Object.values(infoWindowsRef.current).forEach(window => window.close());
      infoWindowsRef.current[partnerId].open(googleMapRef.current, marker);

      // Set as selected
      setSelectedPartner(partnerId);
    }
  };

  // Filter active partners for the sidebar based on search
  const filteredPartners = partners.filter(p => {
    const matchesSearch = searchTerm === "" ||
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.location.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const activePartners = filteredPartners.filter(p => p.active);
  const inactivePartners = filteredPartners.filter(p => !p.active);

  return (
    <div className="space-y-4 animate-fade-in h-[calc(100vh-120px)] flex flex-col">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 flex-none">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 shadow-md">
            <Map className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900">Χάρτης Συνεργατών</h1>
            <p className="text-sm text-slate-500">
              Γεωγραφική επισκόπηση και κάλυψη δικτύου
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 flex-1 min-h-0">
        {/* Sidebar with partners list */}
        <div className="lg:col-span-1 flex flex-col gap-4 h-full min-h-0">
          <Card className="shadow-sm border-slate-200 flex flex-col h-full overflow-hidden">
            <CardHeader className="pb-3 px-4 pt-4 border-b border-slate-100 bg-slate-50/50 flex-none">
              <div className="flex justify-between items-center mb-3">
                <CardTitle className="text-base font-semibold">Λίστα Συνεργατών</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="h-8 w-8 p-0 text-slate-500 hover:text-blue-600"
                >
                  <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Αναζήτηση..."
                  className="pl-8 h-9 bg-white"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent className="p-0 flex-1 overflow-hidden relative">
              <ScrollArea className="h-full">
                <div className="p-3 space-y-4">
                  {partnersLoading ? (
                    <div className="flex flex-col items-center justify-center py-8 text-slate-400">
                      <RefreshCw className="animate-spin h-6 w-6 mb-2" />
                      <span className="text-sm">Φόρτωση...</span>
                    </div>
                  ) : (
                    <>
                      {activePartners.length > 0 && (
                        <div>
                          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">
                            ΕΝΕΡΓΟΙ ({activePartners.length})
                          </h3>
                          <div className="space-y-2">
                            {activePartners.map(partner => (
                              <div
                                key={partner.id}
                                className={`group p-3 rounded-lg cursor-pointer transition-all border ${selectedPartner === partner.id
                                    ? 'bg-blue-50 border-blue-200 shadow-sm'
                                    : 'bg-white border-transparent hover:border-slate-200 hover:bg-slate-50'
                                  }`}
                                onClick={() => zoomToPartner(partner.id)}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className={`h-2 w-2 rounded-full ${partner.active ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                    <div className={`font-medium text-sm ${selectedPartner === partner.id ? 'text-blue-700' : 'text-slate-700'}`}>
                                      {partner.name}
                                    </div>
                                  </div>
                                  {selectedPartner === partner.id && <CheckCircle2 className="h-4 w-4 text-blue-500" />}
                                </div>
                                <div className="flex items-center gap-1 mt-1 pl-4 text-xs text-slate-500 truncate">
                                  <MapPin className="h-3 w-3" />
                                  <span>{partner.location}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {inactivePartners.length > 0 && (
                        <div>
                          <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 mt-4 px-1">
                            ΑΝΕΝΕΡΓΟΙ ({inactivePartners.length})
                          </h3>
                          <div className="space-y-2">
                            {inactivePartners.map(partner => (
                              <div
                                key={partner.id}
                                className={`group p-3 rounded-lg cursor-pointer transition-all border ${selectedPartner === partner.id
                                    ? 'bg-slate-100 border-slate-300'
                                    : 'bg-white border-transparent hover:border-slate-200 hover:bg-slate-50 opacity-70 hover:opacity-100'
                                  }`}
                                onClick={() => zoomToPartner(partner.id)}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className="h-2 w-2 rounded-full bg-slate-300" />
                                    <div className="font-medium text-sm text-slate-600">
                                      {partner.name}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 mt-1 pl-4 text-xs text-slate-500 truncate">
                                  <MapPin className="h-3 w-3" />
                                  <span>{partner.location}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {partners.length === 0 && (
                        <div className="text-center py-8 px-4 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-200 m-2">
                          <User className="h-8 w-8 mx-auto text-slate-300 mb-2" />
                          <p>Δεν βρέθηκαν συνεργάτες</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          <Card className="shadow-sm border-slate-200 flex-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-emerald-500 border border-emerald-600"></div>
                  <span>Ενεργός</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-slate-400 border border-slate-500"></div>
                  <span>Ανενεργός</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-2.5 w-2.5 rounded-full bg-blue-100 border border-blue-400"></div>
                  <span>Εμβέλεια</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main map area */}
        <div className="lg:col-span-3 h-full min-h-0">
          <Card className="shadow-md border-slate-200 h-full overflow-hidden flex flex-col">
            <CardContent className="p-0 flex-1 relative">
              <div
                ref={mapRef}
                className="w-full h-full bg-slate-100"
              />

              {!mapsLoaded && (
                <div className="absolute inset-0 flex items-center justify-center bg-slate-50/80 z-20">
                  <div className="flex flex-col items-center">
                    <RefreshCw className="h-8 w-8 text-blue-500 animate-spin mb-2" />
                    <p className="text-slate-600 font-medium">Φόρτωση Χάρτη...</p>
                  </div>
                </div>
              )}

              {selectedPartner && (
                <Button
                  variant="secondary"
                  size="sm"
                  className="absolute top-4 right-14 z-10 shadow-lg bg-white/90 hover:bg-white backdrop-blur-sm border border-slate-200 text-slate-700"
                  onClick={clearSelection}
                >
                  <X className="h-4 w-4 mr-2" /> Κλείσιμο
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PartnersMap;
